import { D, roundFinal } from './money';
import { selectQuantity } from './fifoSelection';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';

// A position is the paper on the account in its currency: one ticker in USD and EUR is two positions (A-11).
export const positionKey = (p) => `${p.investingAccountId}:${p.ticker}:${p.currency}`;

const TRADING = 'TRADING';

const soldQuantity = (selection, pos) => {
  const q = selection?.[positionKey(pos)];
  return q === undefined || q === null || q === '' ? D(0) : D(q);
};

// Selected sold quantities with their FIFO split, in table order.
export const selectedPositions = (snapshot, selection) =>
  snapshot.positions
    .filter((pos) => soldQuantity(selection, pos).gt(0) && pos.price !== null)
    .map((pos) => ({
      pos,
      key: positionKey(pos),
      sel: selectQuantity(pos, soldQuantity(selection, pos).toFixed()),
    }))
    .filter(({ sel }) => sel.ok && D(sel.quantity).gt(0));

const tradingBucket = (snapshot) =>
  snapshot.realized.buckets.find((b) => b.bucket === TRADING);

const sumAmounts = (taxes, taxType) =>
  taxes.filter((t) => t.taxType === taxType).reduce((acc, t) => acc.plus(t.amount), D(0));

// The single tax formula: max(0, result) * trading rate, rounded per tax type.
export const tradingTax = (snapshot, result) => {
  const { taxTypes, rounding } = jurisdiction;
  const base = D(result).lt(0) ? D(0) : D(result);
  const rates = tradingBucket(snapshot).rates;
  return Object.fromEntries(
    taxTypes.map((type) => [
      type,
      roundFinal(
        rates.filter((r) => r.taxType === type).reduce((acc, r) => acc.plus(base.times(r.rate)), D(0)),
        rounding,
      ),
    ]),
  );
};

const withTotal = (byType, taxTypes, rounding) => {
  const out = {};
  let total = D(0);
  taxTypes.forEach((type) => {
    out[type] = byType[type].toFixed();
    total = total.plus(byType[type]);
  });
  out.total = roundFinal(total, rounding).toFixed();
  return out;
};

export const computeTotals = (snapshot, selection, loss) => {
  const { taxTypes, rounding } = jurisdiction;
  const { buckets } = snapshot.realized;
  const trading = tradingBucket(snapshot);
  const lossD = D(loss || 0);
  const selected = selectedPositions(snapshot, selection);
  const selectedResult = selected.reduce((acc, { sel }) => acc.plus(sel.resultUah), D(0));

  const baseResult = D(trading.income).minus(lossD);
  const afterSale = baseResult.plus(selectedResult);

  const fixed = (type) =>
    buckets
      .filter((b) => b.bucket !== TRADING)
      .reduce((acc, b) => acc.plus(sumAmounts(b.taxes, type)), D(0));
  const liability = (result) => {
    const trade = tradingTax(snapshot, result);
    return withTotal(
      Object.fromEntries(taxTypes.map((type) => [type, roundFinal(trade[type].plus(fixed(type)), rounding)])),
      taxTypes,
      rounding,
    );
  };

  const current = liability(baseResult);
  const possible = liability(afterSale);
  const reduced = D(current.total).minus(possible.total);

  const bucketView = (result) => (b) => {
    if (b.bucket !== TRADING) {
      return withTotal(
        Object.fromEntries(taxTypes.map((t) => [t, roundFinal(sumAmounts(b.taxes, t), rounding)])),
        taxTypes,
        rounding,
      );
    }
    return withTotal(tradingTax(snapshot, result), taxTypes, rounding);
  };

  return {
    current,
    possible,
    reducedBy: roundFinal(reduced, rounding).toFixed(),
    taxIncrease: reduced.lt(0),
    tradingResult: D(trading.income).toFixed(),
    selectedResult: roundFinal(selectedResult, rounding).toFixed(),
    afterSale: roundFinal(afterSale.plus(0), rounding).toFixed(),
    carryForward: roundFinal(afterSale.lt(0) ? afterSale.neg() : D(0), rounding).toFixed(),
    selectedCount: selected.length,
    noPriceCount: snapshot.positions.filter((p) => p.price === null).length,
    buckets: buckets.map((b) => ({
      bucket: b.bucket,
      reducible: b.reducible,
      current: bucketView(baseResult)(b),
      possible: bucketView(afterSale)(b),
    })),
  };
};
